import React, { memo } from 'react';
import { AppNode } from '@/types';
import { useModelStore } from '@/stores/modelStore';
import { InverseScaleBox } from './InverseScaleBox';

interface ConfirmPanelProps {
  data: AppNode;
  cardHeight: number;
  title: string;
  modelFilter: string;
  onNodeUpdate: (id: string, data: Partial<AppNode['data']>) => void;
  onConfirm: () => void;
  onClose: () => void;
  extraContent?: React.ReactNode;
}

const CloseIcon = () => (
  <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
    <path d="M2 2L10 10M10 2L2 10" stroke="white" strokeWidth="1.5" strokeLinecap="round"/>
  </svg>
);

const ConfirmIcon = () => (
  <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
    <path d="M7 1L7 13M7 1L13 7M7 1L1 7" stroke="#1c1c1e" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

export const ConfirmPanel: React.FC<ConfirmPanelProps> = memo(({
  data,
  cardHeight,
  title,
  modelFilter,
  onNodeUpdate,
  onConfirm,
  onClose,
  extraContent,
}) => {
  const currentModel = useModelStore.getState().getModelById(data.data.model || '');
  const supportedResolutions = currentModel?.supportedResolutions;
  const allResolutions = ['1k', '2k', '4k'] as const;

  return (
    <InverseScaleBox
      className="absolute left-1/2 z-30"
      style={{
        top: cardHeight + 8,
        transform: 'translateX(-50%)',
        transformOrigin: 'center top',
      }}
      scaleOrigin="center top"
    >
      <div className="flex items-center gap-4 bg-[#1c1c1e]/95 backdrop-blur-xl border border-white/10 rounded-xl px-5 py-3 shadow-lg min-w-[560px]">
        {/* 关闭按钮 */}
        <button
          onClick={onClose}
          onMouseDown={(e) => e.stopPropagation()}
          className="flex items-center justify-center w-5 h-5 rounded-full hover:bg-white/10 transition-colors shrink-0"
        >
          <CloseIcon />
        </button>

        {/* 标题 */}
        <span className="text-sm font-medium text-white whitespace-nowrap">{title}</span>

        <div className="w-px h-5 bg-white/10 mx-1" />

        {/* 模型选择器 */}
        <div className="flex items-center gap-2 flex-1">
          <span className="text-sm font-medium text-white whitespace-nowrap">模型</span>
          <select
            value={data.data.model || ''}
            onChange={(e) => onNodeUpdate(data.id, { model: e.target.value })}
            onClick={(e) => e.stopPropagation()}
            className="flex-1 min-w-0 bg-white/5 border border-white/10 rounded-lg px-2 py-1 text-sm text-white outline-none focus:border-white/30"
          >
            <option value="" className="bg-[#2c2c2e]">请选择模型</option>
            {useModelStore.getState().getModelsByMode(modelFilter).map((m) => (
              <option key={m.id} value={m.id} className="bg-[#2c2c2e]">{m.name}</option>
            ))}
          </select>
        </div>

        <div className="w-px h-5 bg-white/10 mx-1" />

        {/* 质量选择器 */}
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium text-white whitespace-nowrap">质量</span>
          <div className="flex items-center bg-white/5 border border-white/10 rounded-lg overflow-hidden">
            {allResolutions.map((q) => {
              const isSupported = !supportedResolutions || supportedResolutions.length === 0 || supportedResolutions.includes(q);
              const isActive = (data.data.resolution || '1k') === q;
              return (
                <button
                  key={q}
                  disabled={!isSupported}
                  onMouseDown={(e) => e.stopPropagation()}
                  onClick={() => isSupported && onNodeUpdate(data.id, { resolution: q })}
                  className={`px-2.5 py-1 text-sm font-medium transition-colors ${
                    !isSupported
                      ? 'text-white/15 cursor-not-allowed'
                      : isActive
                        ? 'bg-white/15 text-white'
                        : 'text-white/50 hover:text-white/80'
                  }`}
                >
                  {q.toUpperCase()}
                </button>
              );
            })}
          </div>
        </div>

        {/* 额外内容 */}
        {extraContent}

        {/* 确认按钮 */}
        <button
          onClick={onConfirm}
          onMouseDown={(e) => e.stopPropagation()}
          className="flex items-center justify-center w-8 h-8 bg-white rounded-lg hover:bg-white/90 transition-colors shrink-0"
        >
          <ConfirmIcon />
        </button>
      </div>
    </InverseScaleBox>
  );
});

ConfirmPanel.displayName = 'ConfirmPanel';
